import { lineGoodsAmount, percentLineDiscount } from '@tcr-erp/shared';
import type { FulfillmentMode, OrderItem } from '../types';

/**
 * TCR-2129 — CK khác chính sách (kênh Sỉ/GT/MT). Chỉ để HIỂN THỊ + chặn sớm; BE tự tính lại
 * chính sách và là bên quyết định (`B2BDiscountPolicyService`). Công thức khớp BE
 * `order-discount-policy.utils.ts`: áp trên Giá áp dụng (`item.price`), lệch khi |chênh| ≥ 1đ.
 */
export interface PolicyDiscount { type: 'PERCENT' | 'VND' | null; value: number | null; amount?: number }

export const DISCOUNT_POLICY_CHANNELS = ['SI', 'GT', 'MT'] as const;

export function isDiscountPolicyChannel(ch?: string | null): boolean {
  return !!ch && (DISCOUNT_POLICY_CHANNELS as readonly string[]).includes(ch);
}

// Cùng cơ sở số lượng với `recalcItem`: dòng số lượng/đặt trước → discountAmount là cả dòng;
// dòng serial (giao ngay) → discountAmount là của 1 đơn vị.
function lineQty(item: OrderItem, mode: FulfillmentMode): number {
  return item.trackingMode === 'quantity' || mode === 'PREORDER' ? item.quantity || 1 : 1;
}

export function getPolicyAmount(item: OrderItem, mode: FulfillmentMode): number {
  const p = item.policyDiscount;
  const value = Number(p?.value ?? 0);
  if (!p?.type || value <= 0 || item.price <= 0) return 0;
  const qty = lineQty(item, mode);
  // TCR-2224 — CK % theo chuẩn MISA, cùng công thức với `recalcItem`: làm tròn (Thành tiền × %),
  // Thành tiền = làm tròn (Đơn giá × SL). Lệch công thức là báo "Sửa CK" giả 1đ với đơn giá lẻ.
  return p.type === 'PERCENT'
    ? percentLineDiscount(lineGoodsAmount(item.price, qty), Math.min(value, 100))
    : Math.round(Math.min(value, item.price) * qty);
}

export function getActualDiscountAmount(item: OrderItem, _mode: FulfillmentMode): number {
  return Math.max(0, Math.round(Number(item.discountAmount ?? 0)));
}

export function getDeviationAmount(item: OrderItem, mode: FulfillmentMode): number {
  if (!item.policyDiscount || item.comboGroupId) return 0;
  return Math.round(getActualDiscountAmount(item, mode) - getPolicyAmount(item, mode));
}

export function isDiscountDeviating(item: OrderItem, mode: FulfillmentMode): boolean {
  return Math.abs(getDeviationAmount(item, mode)) >= 1;
}

// Không dùng toLocaleString('vi-VN') vì phụ thuộc ICU của môi trường chạy.
const vnd = (n: number) => `${String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}đ`;

/** Giá trị CS không tiền tố ("2%", "5.000đ", "0%"). */
export function formatPolicyValue(p?: PolicyDiscount | null): string {
  const value = Number(p?.value ?? 0);
  if (!p?.type || value <= 0) return '0%';
  return p.type === 'PERCENT' ? `${value}%` : vnd(value);
}

export function formatPolicyLabel(p?: PolicyDiscount | null): string {
  return `CS: ${formatPolicyValue(p)}`;
}

export function findMissingDiscountReasons(items: OrderItem[], mode: FulfillmentMode): OrderItem[] {
  return items.filter((i) => isDiscountDeviating(i, mode) && !(i.overrideReason ?? '').trim());
}
